/* eslint-disable @typescript-eslint/no-explicit-any */
import { AffinisBooleanDialogComponent, BooleanDialogData } from '../boolean-dialog';
import { AffinisHeaderColumn, AffinisCellTemplateKey } from './data-table.enums';
import { AffinisGridColumn, SelectOption, EditSelectOption } from './models';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { PAGINATION_DEFAULTS } from '@shared/constants';

@Component({
  selector: 'affinis-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
})
export class AffinisDataTableComponent implements OnChanges, OnInit {
  @ViewChild('checkboxTemplate', { static: true })
  public checkboxTemplateRef!: TemplateRef<void>;

  @ViewChild('editTemplate', { static: true })
  public editTemplateRef!: TemplateRef<void>;

  @ViewChild('inputTemplate', { static: true })
  public inputTemplateRef!: TemplateRef<void>;

  @ViewChild('numberTemplate', { static: true })
  public numberTemplateRef!: TemplateRef<void>;

  @ViewChild('labelTemplate', { static: true })
  public labelTemplateRef!: TemplateRef<void>;

  @ViewChild('selectTemplate', { static: true })
  public selectTemplateRef!: TemplateRef<void>;

  @ViewChild('multiSelectTemplate', { static: true })
  public multiSelectTemplateRef!: TemplateRef<void>;

  @ViewChild('headerTemplate', { static: true })
  public headerTemplateRef!: TemplateRef<void>;

  @Input() public tableColumns: AffinisGridColumn[] = [];

  @Input()
  public editObject: any = { id: '' };

  @Input()
  public editObjects: any[] = [];

  @Output() public editObjectChange = new EventEmitter();

  @Input() public headerExtraTemplate!: TemplateRef<void>;

  @Input() public expandable = false;

  @Input() public expansionTemplate!: TemplateRef<void>;

  @Input() public editAllEntries = false;

  @Input() public isDeletable = true;

  @Input() public isLoading = false;

  @Input() public filterOnFront = true;

  @Input() public pageOnFront = true;

  @Input() public pageIndex = PAGINATION_DEFAULTS.PAGE_INDEX;

  @Input() public pageSize = PAGINATION_DEFAULTS.PAGE_SIZE;

  @Input() public totalCount = 0;

  @Input() public tableData: any[] = [];

  @Input() public editSelectOptions!: EditSelectOption[];

  @Input() public rowHover = false;

  @Input() public rowSelectable = false;

  @Input() public multiSelectable = true;

  @Input()
  public dependantEditOptions: Record<string, SelectOption[]> = {};

  @Input() public showPaginator = true;

  @Output() public canceledRow = new EventEmitter<string>();

  @Output() public deletedRow = new EventEmitter<string>();

  @Output() public savedRow = new EventEmitter();

  @Output() public rowClick = new EventEmitter();

  @Output() public changedPage = new EventEmitter<PageEvent>();

  @Output() public rowSelectionChange = new EventEmitter<any>();

  @Output() public editObjectsChange = new EventEmitter<any[]>();

  public filteredData: any[] = [];

  public filterValues: Record<string, any> = {};

  public headerColumn: typeof AffinisHeaderColumn = AffinisHeaderColumn;

  constructor(
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    public translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        if (propName === 'tableData') {
          this.filterTable();
        }
      }
    }
  }

  ngOnInit(): void {
    const templateRefs = [
      { key: AffinisCellTemplateKey.Checkbox, value: this.checkboxTemplateRef },
      { key: AffinisCellTemplateKey.Edit, value: this.editTemplateRef },
      { key: AffinisCellTemplateKey.Input, value: this.inputTemplateRef },
      { key: AffinisCellTemplateKey.Label, value: this.labelTemplateRef },
      { key: AffinisCellTemplateKey.Select, value: this.selectTemplateRef },
      { key: AffinisCellTemplateKey.MultiSelect, value: this.multiSelectTemplateRef },
      { key: AffinisCellTemplateKey.Number, value: this.numberTemplateRef },
    ];
    for (const column of this.tableColumns) {
      if (column.cellTemplateKey) {
        column.cellTemplate = templateRefs.find(x => x.key === column.cellTemplateKey)?.value;
      }
      if (column.headerType === AffinisHeaderColumn.Filter) {
        this.filterValues[column.field] = '';
      }
      if (column.headerType === AffinisHeaderColumn.MultiSelectFilter) {
        this.filterValues[column.field] = [];
      }
    }
    if (this.tableData !== undefined) {
      this.filteredData = this.tableData.slice(0);
    }
    this.cd.detectChanges();

    this.headerExtraTemplate = this.headerExtraTemplate || this.headerTemplateRef;
  }

  public changePage(e: PageEvent) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.changedPage.emit(e);
  }

  public changeSort(s: Sort) {
    if (s.direction === '') {
      this.filterTable();
      return;
    }

    const sortingColumn = this.tableColumns.find(x => x.field === s.active);

    if (
      !sortingColumn?.cellTemplate ||
      !sortingColumn?.cellTemplateKey ||
      sortingColumn?.cellTemplateKey === AffinisCellTemplateKey.Input ||
      sortingColumn?.cellTemplateKey === AffinisCellTemplateKey.Number ||
      sortingColumn?.cellTemplateKey === AffinisCellTemplateKey.Checkbox
    ) {
      this.filteredData.sort((a: any, b: any) => {
        const valueA = typeof a[s.active] === 'string' ? a[s.active]?.toLowerCase() : a[s.active];
        const valueB = typeof a[s.active] === 'string' ? b[s.active]?.toLowerCase() : b[s.active];
        return this.compare(valueA, valueB, s.direction);
      });
    } else if (sortingColumn?.cellTemplateKey === AffinisCellTemplateKey.Select) {
      this.filteredData.sort((a: any, b: any) => {
        const options = this.editSelectOptions.find(x => x.key === s.active)?.options;
        if (options) {
          const valueA = options.find(x => x.id === a[s.active])?.name.toLowerCase();
          const valueB = options.find(x => x.id === b[s.active])?.name.toLowerCase();
          return this.compare(valueA, valueB, s.direction);
        }
        return 0;
      });
    }
    this.filteredData = this.filteredData.slice(0);
  }

  public deleteRow(id: string) {
    const data: BooleanDialogData = {
      title: this.translateService.instant('dialog.deleteRow.title'),
      message: '',
      question: this.translateService.instant('dialog.deleteRow.question'),
    };

    const dialogRef = this.dialog.open(AffinisBooleanDialogComponent, { data });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deletedRow.emit(id);
      }
    });
  }

  public editRow(editObject: any) {
    this.editObject = structuredClone(editObject);
    this.editObjectChange.emit(this.editObject);
    this.cd.detectChanges();
  }

  public cancelEdit() {
    this.canceledRow.emit(this.editObject.id);
    this.editObject = { id: '' };
    this.editObjectChange.emit(this.editObject);
  }

  public saveRow() {
    this.removeWrongDependantProperties(this.editObject);
    this.savedRow.emit(this.editObject);
  }

  public onRowClick(event: any) {
    this.rowClick.emit(event);
  }

  public onRowSelectionChange($event: any) {
    this.rowSelectionChange.emit($event);
  }

  //TODO Code Smell: Refactor this function to reduce its Cognitive Complexity
  public filterTable() {
    if (this.tableData) {
      let shownData = this.tableData.slice(0);
      if (this.filterOnFront) {
        for (const key of Object.keys(this.filterValues)) {
          if (
            this.filterValues[key] == null ||
            this.filterValues[key] === '' ||
            this.filterValues[key].length === 0
          ) {
            continue;
          }
          shownData = shownData.filter(x => {
            const data = x[key];
            if (Array.isArray(this.filterValues[key])) {
              return Array.isArray(data)
                ? data.some(y => this.filterValues[key].includes(y))
                : this.filterValues[key].includes(data);
            }

            if (typeof data === 'string') {
              return data.toLowerCase().includes(this.filterValues[key].toLowerCase());
            }

            if (typeof data === 'number') {
              return data == this.filterValues[key].replace(',', '.');
            }
          });
        }
      }
      this.filteredData = shownData;
    }
  }

  private compare(valueA: any, valueB: any, direction: string): number {
    let comparatorResult = 0;
    if (valueA != null && valueB != null) {
      if (valueA > valueB) {
        comparatorResult = 1;
      } else if (valueA < valueB) {
        comparatorResult = -1;
      }
    } else if (valueA != null) {
      comparatorResult = 1;
    } else if (valueB != null) {
      comparatorResult = -1;
    }
    return comparatorResult * (direction == 'asc' ? 1 : -1);
  }

  private removeWrongDependantProperties(object: any) {
    this.tableColumns
      .filter(x => x.dependantSelect)
      .forEach(column => {
        const value = object[column.field];
        const dependantOptions =
          this.dependantEditOptions[object[column.dependantSelectAttribute ?? '']];

        if (!dependantOptions.some(x => x.id === value)) {
          object[column.field] = '';
        }
      });
  }
}
